import { useState } from 'react';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';

import { useCopyToClipboard } from '@hooks/useCopyToClipboard';

interface MaskedTokenFieldProps {
  label: string;
  token: string;
  /** Short note under the label, e.g. who the token is for. */
  hint?: string;
}

const MASK = '•'.repeat(40);

/**
 * A secret bearer token shown masked by default, with reveal and copy
 * affordances. There's no shared secret-reveal component in the app, so this
 * pairs the standard copy pattern (useCopyToClipboard) with a show/hide toggle.
 */
export function MaskedTokenField({ label, token, hint }: MaskedTokenFieldProps) {
  const [revealed, setRevealed] = useState(false);
  const copy = useCopyToClipboard();

  return (
    <div>
      <div className="text-sm font-medium text-gray-700">{label}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
      <div className="mt-1 flex items-center gap-1">
        <span className="flex-1 break-all font-mono text-xs text-gray-700">
          {revealed ? token : MASK}
        </span>
        <Tooltip title={revealed ? 'Hide' : 'Reveal'}>
          <IconButton size="small" onClick={() => setRevealed(r => !r)}>
            {revealed ? (
              <VisibilityOff fontSize="inherit" />
            ) : (
              <Visibility fontSize="inherit" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title={`Copy ${label}`}>
          <IconButton size="small" onClick={() => copy(token, label)}>
            <ContentCopy fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}
